// Säkerhetskopiering av projektets dokument och skyddsronder till en extern
// lagringsplats (SharePoint/OneDrive) så att inget underlag kan saknas.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle2, CloudUpload, HardDriveDownload, Loader2 } from "lucide-react";
import {
  listBackupDestinations,
  listBackupHistory,
  runProjectBackup,
  type BackupProgress,
  type BackupResult,
} from "@/lib/project/backup";
import type { DocumentDestination, SyncLogRow } from "@/lib/integrations/destinations";
import { getProvider } from "@/lib/integrations/providers";

interface Props {
  projectId: string;
  /** Kompakt läge används inuti nedladdningsdialogen. */
  compact?: boolean;
}

const LOCAL_VALUE = "local";

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const BackupPanel: React.FC<Props> = ({ projectId, compact }) => {
  const [destinations, setDestinations] = useState<DocumentDestination[]>([]);
  const [history, setHistory] = useState<SyncLogRow[]>([]);
  const [target, setTarget] = useState<string>(LOCAL_VALUE);
  const [progress, setProgress] = useState<BackupProgress | null>(null);
  const [result, setResult] = useState<BackupResult | null>(null);

  const load = useCallback(async () => {
    const [dest, log] = await Promise.all([
      listBackupDestinations(projectId),
      listBackupHistory(projectId),
    ]);
    setDestinations(dest);
    setHistory(log);
    setTarget((prev) => (prev === LOCAL_VALUE && dest.length ? dest[0].id : prev));
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const lastOk = history.find((h) => h.status === "success");
  const busy = progress !== null;

  const handleBackup = async () => {
    setResult(null);
    setProgress({ current: 0, total: 1, label: "Startar…" });
    try {
      const res = await runProjectBackup({
        projectId,
        destinationId: target === LOCAL_VALUE ? null : target,
        onProgress: setProgress,
      });
      setResult(res);
      if (res.blob) {
        // Ingen extern plats vald, eller uppladdningen misslyckades – spara lokalt
        // så att kopian aldrig går förlorad.
        triggerDownload(res.blob, res.filename);
      }
      if (res.ok && target !== LOCAL_VALUE) {
        toast.success(`Säkerhetskopia med ${res.fileCount} filer uppladdad`);
      } else if (res.ok) {
        toast.success(`Säkerhetskopia med ${res.fileCount} filer skapad`);
      } else {
        toast.error(`Uppladdningen misslyckades: ${res.error ?? "okänt fel"}. Kopian laddades ner lokalt.`);
      }
      await load();
    } catch (err) {
      toast.error(`Kunde inte skapa säkerhetskopian: ${(err as Error).message}`);
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className={compact ? "space-y-3 rounded-md border p-3" : "space-y-4"}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">Säkerhetskopiering</p>
          <p className="text-xs text-muted-foreground">
            Alla dokument, skyddsronder och rådata paketeras och läggs i mappen
            Sakerhetskopior på vald lagringsplats.
          </p>
        </div>
        {lastOk ? (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Senast {new Date(lastOk.synced_at).toLocaleDateString("sv-SE")}
          </Badge>
        ) : (
          <Badge variant="outline">Ingen kopia ännu</Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={target} onValueChange={setTarget} disabled={busy}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Välj lagringsplats" />
          </SelectTrigger>
          <SelectContent>
              {destinations.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.label || getProvider(d.provider || d.connector_type).name}
                </SelectItem>
              ))}
            <SelectItem value={LOCAL_VALUE}>Ladda ner lokalt i stället</SelectItem>
          </SelectContent>
        </Select>

        <Button onClick={handleBackup} disabled={busy}>
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : target === LOCAL_VALUE ? (
            <HardDriveDownload className="mr-2 h-4 w-4" />
          ) : (
            <CloudUpload className="mr-2 h-4 w-4" />
          )}
          {busy ? "Säkerhetskopierar…" : "Säkerhetskopiera nu"}
        </Button>
      </div>

      {!destinations.length && (
        <p className="text-xs text-muted-foreground">
          Ingen extern lagringsplats är ansluten för projektet. Anslut en destination under
          Integrationer för att lägga kopian där automatiskt.
        </p>
      )}

      {busy && (
        <div className="space-y-1">
          <Progress value={(progress.current / Math.max(progress.total, 1)) * 100} />
          <p className="text-xs text-muted-foreground">{progress.label}</p>
        </div>
      )}

      {result && (
        <Alert variant={result.ok ? "default" : "destructive"}>
          {result.ok ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertTitle>
            {result.ok
              ? `${result.fileCount} filer i säkerhetskopian`
              : "Uppladdningen misslyckades"}
          </AlertTitle>
          <AlertDescription className="space-y-1 text-sm">
            {!result.ok && <p>{result.error}. Kopian laddades ner lokalt i stället.</p>}
            {result.failed.length > 0 && (
              <p>Kunde inte skapas: {result.failed.join(", ")}</p>
            )}
            {result.missing.length > 0 ? (
              <details>
                <summary className="cursor-pointer">
                  {result.missing.length} dokument saknar underlag och ingår inte
                </summary>
                <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {result.missing.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </details>
            ) : (
              <p>Inget underlag saknas – hela projektet är kopierat.</p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {!compact && history.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Historik
          </p>
          <ul className="space-y-1 text-xs">
            {history.slice(0, 10).map((h) => (
              <li key={h.id} className="flex items-center gap-2">
                <Badge variant={h.status === "success" ? "secondary" : "destructive"}>
                  {h.status === "success" ? "OK" : "Fel"}
                </Badge>
                <span>{new Date(h.synced_at).toLocaleString("sv-SE")}</span>
                <span className="text-muted-foreground">{h.filename}</span>
                {h.error_message && (
                  <span className="text-destructive">{h.error_message}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BackupPanel;
